import logger from 'ws-scripts/modules/logger';

/**
 * Subscribes to vehicle data updates and retrieves vehicle and dealer information.
 * Uses a timeout to prevent hanging if the event never fires.
 *
 * @param {number} [timeout=10000] - Maximum time to wait for vehicle data (ms)
 * @returns {Promise<{vehicle: Object, dealer: Object}>} Vehicle and dealer data
 * @throws {Error} If APILoader is unavailable, timeout occurs, or API call fails
 */
export const getAPIVehicleInfo = (timeout = 10000) => {
	return new Promise((resolve, reject) => {
		// Timeout safety net
		const timeoutId = setTimeout(() => {
			reject(new Error(`Vehicle info fetch timeout after ${timeout} ms`));
		}, timeout);

		// Guard clause for missing APILoader
		if (!window?.DDC?.APILoader) {
			clearTimeout(timeoutId);
			reject(new Error('DDC APILoader not available'));
			return;
		}

		(async (APILoader) => {
			try {
				const wiAPI = await APILoader.create();

				if (!wiAPI) {
					clearTimeout(timeoutId);
					reject(new Error('Failed to create wiAPI'));
					return;
				}

				const dealerData = await wiAPI.utils?.getDealerData();

				let hasResolved = false;
				const unsubscribe = wiAPI.subscribe(
					'vehicle-data-updated-v1',
					(eventData) => {
						if (hasResolved) return; // Prevent multiple resolutions
						hasResolved = true;
						clearTimeout(timeoutId);
						if (typeof unsubscribe === 'function') {
							unsubscribe(); // Cleanup subscription
						}
						resolve({
							vehicle: eventData?.payload?.vehicleData?.[0],
							dealer: dealerData
						});
					}
				);

				// Warn if subscribe doesn't return unsubscribe function
				if (typeof unsubscribe !== 'function') {
					logger.warn(
						'wiAPI.subscribe did not return unsubscribe function'
					);
				}
			} catch (err) {
				clearTimeout(timeoutId);
				reject(new Error(`Error in getAPIVehicleInfo: ${err.message}`));
			}
		})(window.DDC.APILoader);
	});
};

/**
 * Get dealer MMD ID from vehicle/dealer data in priority order.
 *
 * Priority:
 * 1. dealerData.dealershipCodes.mmd (website dealer codes)
 * 2. vehicle.dealerCodes.mmd (vehicle dealer codes)
 * 3. window.DDC.PrivateAPI.content['digital-retailing'].dealerId (fallback)
 *
 * @param {Object} vehicle - Vehicle object with dealerCodes
 * @param {Object} dealerData - Dealer data with dealershipCodes
 * @returns {number} MMD ID as integer, or NaN if all sources fail
 */
export const getMmdId = (vehicle, dealerData) => {
	return (
		parseInt(dealerData?.dealershipCodes?.mmd, 10) ||
		parseInt(vehicle?.dealerCodes?.mmd, 10) ||
		parseInt(
			window?.DDC?.PrivateAPI?.content?.['digital-retailing']?.dealerId,
			10
		)
	);
};
